// PATH: src/components/trials/TrialSettingsForm.tsx
import * as React from "react";
import { Autocomplete, Box, Button, Card, Divider, FormControlLabel, Stack, Switch, TextField, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import type { SettingOrigin, TrialOverride, TrialResolved, TrialRole } from "../../models/domain";
import { StatusPill, type PillTone } from "../ui";
import InstructionChain from "../settings/InstructionChain";
import type { RolePrompts } from "../settings/rolePrompts";
import { MAX_INSTRUCTIONS, ROLE_LABELS, fieldOrigin, originLabel, type TrialField, type TrialForm } from "./trialLogic";

interface Props {
  form: TrialForm;
  onChange: (form: TrialForm) => void;
  /** Valeurs effectives de la matière (estimation à surcharge vide). */
  base: TrialResolved;
  override: TrialOverride;
  models: string[];
  /** Le prompt de base de chaque rôle, affiché avant les consignes. */
  prompts: Record<TrialRole, RolePrompts>;
  disabled?: boolean;
}

const ORIGIN_TONE: Record<SettingOrigin, PillTone> = { global: "neutral", subject: "info", trial: "primary" };

const Origin: React.FC<{ origin: SettingOrigin | undefined }> = ({ origin }) => (
  <StatusPill tone={ORIGIN_TONE[origin ?? "global"]} title="D'où vient la valeur utilisée par l'essai">
    {originLabel(origin)}
  </StatusPill>
);

const FieldLabel: React.FC<{ label: string; origin: SettingOrigin | undefined }> = ({ label, origin }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
    <Typography variant="subtitle2">{label}</Typography>
    <Origin origin={origin} />
  </Stack>
);

/**
 * Étape 2 : les réglages de l'essai, préremplis avec ceux de la matière. Une
 * pastille dit pour chaque champ d'où vient la valeur : « global »,
 * « matière », ou « essai » dès qu'on la change.
 */
export const TrialSettingsForm: React.FC<Props> = ({ form, onChange, base, override, models, prompts, disabled }) => {
  const origin = (role: TrialRole, field: TrialField) => fieldOrigin(override, base, role, field);
  const reviewOff = !form.review.enabled;

  const modelField = (role: TrialRole, value: string, set: (v: string) => void, off = false) => (
    <Box>
      <FieldLabel label="Modèle" origin={origin(role, "model")} />
      <Autocomplete
        freeSolo
        size="small"
        options={models}
        value={value}
        disabled={disabled || off}
        onInputChange={(_, v) => set(v)}
        renderInput={(p) => (
          <TextField
            {...p}
            placeholder={base[role]?.model ?? ""}
            helperText="Vide = le modèle de la matière."
            inputProps={{ ...p.inputProps, "aria-label": `Modèle — ${ROLE_LABELS[role]}` }}
          />
        )}
      />
    </Box>
  );

  // Le prompt de base, la consigne héritée, puis le champ de l'essai : l'ordre
  // dans lequel le modèle les reçoit.
  const instructionsField = (role: TrialRole, value: string, set: (v: string) => void, off = false) => {
    const inherited = base[role]?.instructions;
    return (
      <InstructionChain
        prompts={prompts[role]}
        inherited={inherited}
        inheritedOrigin={base[role]?.origin?.instructions}
        replaced={override[role]?.instructions !== undefined}
      >
        <TextField
          multiline
          minRows={3}
          maxRows={10}
          fullWidth
          size="small"
          value={value}
          disabled={disabled || off}
          onChange={(e) => set(e.target.value)}
          inputProps={{ maxLength: MAX_INSTRUCTIONS, dir: "auto", "aria-label": `Consigne de l'essai — ${ROLE_LABELS[role]}` }}
          placeholder={inherited ? "Vide : la consigne héritée part telle quelle." : "Vide : le prompt de base seul."}
          helperText={
            `${value.length} / ${MAX_INSTRUCTIONS} · remplie, elle REMPLACE la consigne héritée (elles ne s'additionnent pas) ` +
            "et part seule en fin de message. Vide = la consigne héritée : un essai ne peut pas en retirer une."
          }
        />
        {inherited && (
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            disabled={disabled || off || value.trim() === inherited.trim()}
            onClick={() => set(inherited)}
            sx={{ mt: 0.5 }}
          >
            Partir de la consigne héritée
          </Button>
        )}
      </InstructionChain>
    );
  };

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography variant="h5">{ROLE_LABELS.fact_extraction}</Typography>
          <Typography variant="body2" color="text.secondary">
            Tire du cours les faits de chaque objectif, à température 0.
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2} sx={{ p: 2 }}>
          {modelField("fact_extraction", form.extraction.model, (model) =>
            onChange({ ...form, extraction: { ...form.extraction, model } }),
          )}
          {instructionsField("fact_extraction", form.extraction.instructions, (instructions) =>
            onChange({ ...form, extraction: { ...form.extraction, instructions } }),
          )}
        </Stack>
      </Card>

      <Card variant="outlined">
        <Box sx={{ p: 2 }}>
          <Typography variant="h5">{ROLE_LABELS.fact_review}</Typography>
          <Typography variant="body2" color="text.secondary">
            Vérifie chaque fait contre le cours et écarte ceux qu'il ne soutient pas.
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2} sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={form.review.enabled}
                  color={reviewOff ? "error" : "primary"}
                  disabled={disabled}
                  onChange={(e) => onChange({ ...form, review: { ...form.review, enabled: e.target.checked } })}
                />
              }
              label={
                <Typography variant="body2" color={reviewOff ? "error" : undefined}>
                  {reviewOff ? "Relecture coupée pour l'essai" : "Relecture active"}
                </Typography>
              }
            />
            <Origin origin={origin("fact_review", "enabled")} />
          </Stack>
          {reviewOff && (
            <Typography variant="caption" color="text.secondary">
              Les faits de l'essai ne seront pas vérifiés : aucun verdict, aucun fait écarté.
            </Typography>
          )}
          {modelField(
            "fact_review",
            form.review.model,
            (model) => onChange({ ...form, review: { ...form.review, model } }),
            reviewOff,
          )}
          {instructionsField(
            "fact_review",
            form.review.instructions,
            (instructions) => onChange({ ...form, review: { ...form.review, instructions } }),
            reviewOff,
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default TrialSettingsForm;
